import { registerRoute, initRouter } from './router';
import { isLoggedIn, checkAuth, onAuthChange, getCurrentUser } from './auth';
import { createHeader } from './components/header';
import { createSidebar } from './components/sidebar';

// Pages
import { loginPage } from './pages/login';
import { dashboardPage } from './pages/dashboard';
import { propertiesPage } from './pages/properties';
import { propertyDetailPage } from './pages/property-detail';
import { propertyFormPage } from './pages/property-form';
import { maintenancePage } from './pages/maintenance';
import { maintenanceDetailPage } from './pages/maintenance-detail';
import { tradesmenPage } from './pages/tradesmen';
import { tradesmanDetailPage } from './pages/tradesman-detail';
import { remindersPage } from './pages/reminders';
import { inboundEmailsPage } from './pages/inbound-emails';
import { propertyMePage } from './pages/propertyme';
import { settingsPage } from './pages/settings';
import { financialsPage } from './pages/financials';
import { adminUsersPage } from './pages/admin-users';
import { insurancePage } from './pages/insurance';
import { compliancePage } from './pages/compliance';

const appRoot = document.getElementById('app')!;

function authGuard(handler: (params: Record<string, string>, query?: URLSearchParams) => Promise<HTMLElement>) {
  return async (params: Record<string, string>, query?: URLSearchParams) => {
    if (!isLoggedIn()) {
      window.location.hash = '#/login';
      return document.createElement('div');
    }
    return handler(params, query);
  };
}

function adminGuard(handler: (params: Record<string, string>, query?: URLSearchParams) => Promise<HTMLElement>) {
  return authGuard(async (params, query) => {
    const user = getCurrentUser();
    if (user?.role !== 'admin') {
      window.location.hash = '#/dashboard';
      return document.createElement('div');
    }
    return handler(params, query);
  });
}

// Register routes
registerRoute('/login', async () => {
  if (isLoggedIn()) {
    window.location.hash = '#/dashboard';
    return document.createElement('div');
  }
  return loginPage();
});
registerRoute('/dashboard', authGuard(() => dashboardPage()));
registerRoute('/properties', authGuard(() => propertiesPage()));
registerRoute('/properties/new', authGuard((p) => propertyFormPage({ ...p, id: 'new' })));
registerRoute('/properties/:id/edit', authGuard((p) => propertyFormPage(p)));
registerRoute('/properties/:id', authGuard((p) => propertyDetailPage(p)));
registerRoute('/properties/:propertyId/maintenance/:issueId', authGuard((p) => maintenanceDetailPage(p)));
registerRoute('/maintenance', authGuard(() => maintenancePage()));
registerRoute('/insurance', authGuard(() => insurancePage()));
registerRoute('/compliance', authGuard(() => compliancePage()));
registerRoute('/tradesmen', authGuard(() => tradesmenPage()));
registerRoute('/tradesmen/:id', authGuard((p) => tradesmanDetailPage(p)));
registerRoute('/reminders', authGuard(() => remindersPage()));
registerRoute('/inbound-emails', authGuard(() => inboundEmailsPage()));
registerRoute('/financials', authGuard((_p, q) => financialsPage(undefined, q)));
registerRoute('/propertyme', authGuard(() => propertyMePage()));
registerRoute('/settings', authGuard(() => settingsPage()));
registerRoute('/admin/users', adminGuard(() => adminUsersPage()));

function renderShell(): void {
  appRoot.innerHTML = '';
  if (!isLoggedIn()) {
    const content = document.createElement('div');
    content.id = 'page-content';
    appRoot.appendChild(content);
    initRouter(content);
    return;
  }

  // Authenticated layout
  appRoot.className = 'flex flex-col h-screen';
  appRoot.appendChild(createHeader());

  const body = document.createElement('div');
  body.className = 'flex flex-1 overflow-hidden';

  body.appendChild(createSidebar());

  const main = document.createElement('main');
  main.id = 'page-content';
  main.className = 'flex-1 overflow-y-auto p-6 bg-gray-50';
  body.appendChild(main);

  appRoot.appendChild(body);
  initRouter(main);
}

// Bootstrap
async function init(): Promise<void> {
  // Apply saved theme before rendering
  const savedTheme = localStorage.getItem('theme');
  if (savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }

  if (isLoggedIn()) {
    const valid = await checkAuth();
    if (!valid) {
      window.location.hash = '#/login';
    }
  } else if (window.location.hash !== '#/login') {
    window.location.hash = '#/login';
  }
  renderShell();
}

onAuthChange(() => {
  renderShell();
});

init();
